import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { sanitizeAccountNumber, validAccountNumber } from '@eika-infrastruktur/format';
import { Button, LinkButton } from '@eika/button';
import { Loader } from '@eika/loader';

import useOcr from '../../../../fetchHooks/useOcr';
import { useMutateReceivers } from '../../../../fetchHooks/useReceivers';
import type { Receiver } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { Content } from '../../../shared/Content';
import { ErrorCardWrapper } from '../../../shared/ErrorCardWrapper';
import { toSelectedReceiver } from '../../SelectedReceiver';
import AccountNameField from './AccountNameField';
import AccountNumberField from './AccountNumberField';
import SelectReceiverStickyFooter from './SelectReceiverStickyFooter';
import validateForm, { ValidationMessage } from './validateForm';

type SelectedReceiver = import('../../SelectedReceiver').SelectedReceiver;

enum FieldOrder {
    ACCOUNTNAME_ACCOUNTNUMBER,
    ACCOUNTNUMBER_ACCOUNTNAME,
}

interface Props {
    receivers?: Receiver[];
    searchString: string;
    onChange: (receiverAccount: SelectedReceiver) => void;
    isInPaymentContext?: boolean;
    isNettbank?: boolean;
    onCancel?: () => void;
}

const findAccount = (receivers: Receiver[] | undefined, accountNumber: string) =>
    receivers?.find((receiver) => receiver.accountNumber.value === accountNumber);

const initialValidationMessages = {};

const NewReceiver: React.FC<Props> = ({
    receivers,
    searchString = '',
    onChange,
    onCancel,
    isInPaymentContext = true,
    isNettbank = false,
}) => {
    const translated = useTranslation();
    const [accountName, setAccountName] = useState<string>('');
    const [accountNumber, setAccountNumber] = useState<string>('');
    const accountNumberSanitized = sanitizeAccountNumber(accountNumber);

    const [validationMessages, setValidationMessages] = useState<ValidationMessage>(initialValidationMessages);
    const [duplicateAccountWarning, setDuplicateAccountWarning] = useState<string>();
    const [fieldOrder, setFieldOrder] = useState(FieldOrder.ACCOUNTNAME_ACCOUNTNUMBER);

    const { add: addReceiver } = useMutateReceivers();

    const handleAccountNumberChange = (event: ChangeEvent<HTMLInputElement>) => setAccountNumber(event.target.value);
    const handleAccountNameChange = (event: ChangeEvent<HTMLInputElement>) => setAccountName(event.target.value);

    const accountNumberList = useMemo(() => receivers?.map((account) => account.accountNumber.value), [receivers]);
    const {
        data: ocrInfo,
        isLoading,
        error: ocrLookupError,
        refetch: refetchOcr,
    } = useOcr({ value: accountNumberSanitized }, validAccountNumber(accountNumberSanitized));

    useEffect(() => {
        setDuplicateAccountWarning(
            accountNumberList?.includes(accountNumberSanitized)
                ? format(
                      translated.paymentValidation.existingAccountNumber,
                      findAccount(receivers, accountNumberSanitized)!.name,
                  )
                : undefined,
        );
    }, [accountNumberList, accountNumberSanitized, receivers, translated.paymentValidation.existingAccountNumber]);

    useEffect(() => {
        const hasEnteredAccountNumber = /^[\d. ]+$/.test(searchString);
        if (hasEnteredAccountNumber) {
            setAccountNumber(searchString);
            setFieldOrder(FieldOrder.ACCOUNTNUMBER_ACCOUNTNAME);
        } else {
            setAccountName(searchString);
            setFieldOrder(FieldOrder.ACCOUNTNAME_ACCOUNTNUMBER);
        }
    }, [searchString]);

    useEffect(() => {
        setValidationMessages(initialValidationMessages);
    }, [accountName, accountNumber]);

    const handleSubmit = useCallback(async () => {
        const errorMessages = validateForm({
            accountNumber: accountNumberSanitized,
            accountName,
            translated,
            isInPaymentContext,
            duplicateAccountWarning,
        });
        if (Object.keys(errorMessages).length > 0) {
            setValidationMessages(errorMessages);
            return;
        }

        const newReceiver = toSelectedReceiver(
            {
                accountNumber: {
                    value: accountNumberSanitized,
                },
                name: accountName,
            },
            ocrInfo,
        );

        const existingReceiver = findAccount(receivers, accountNumberSanitized);
        const duplicateReceiver = existingReceiver && toSelectedReceiver(existingReceiver, ocrInfo);
        if (duplicateReceiver) {
            onChange(duplicateReceiver);
            return;
        }

        const messages = validateForm({
            accountNumber: accountNumberSanitized,
            accountName,
            translated,
            ocrName: ocrInfo?.name,
        });
        setValidationMessages(messages);

        const noValidationMessages = Object.keys(messages).length === 0;

        if (noValidationMessages) {
            if (!isInPaymentContext || !duplicateAccountWarning) {
                addReceiver.mutate({ name: accountName, accountNumber: newReceiver.accountNumber });
            }
            onChange(newReceiver);
        }
    }, [
        accountName,
        accountNumberSanitized,
        addReceiver,
        duplicateAccountWarning,
        isInPaymentContext,
        ocrInfo,
        onChange,
        receivers,
        translated,
    ]);

    if (ocrLookupError) {
        return (
            <Content>
                <ErrorCardWrapper exception={ocrLookupError} isModal>
                    <Button onClick={() => refetchOcr()}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <>
            {fieldOrder === FieldOrder.ACCOUNTNAME_ACCOUNTNUMBER ? (
                <>
                    <AccountNameField
                        accountName={accountName}
                        handleAccountNameChange={handleAccountNameChange}
                        translated={translated}
                        validationMessage={validationMessages.accountNameInput}
                        isNettbank
                    />
                    <AccountNumberField
                        accountNumber={accountNumber}
                        handleAccountNumberChange={handleAccountNumberChange}
                        translated={translated}
                        validationMessage={validationMessages.accountNumberInput}
                        attentionMessage={duplicateAccountWarning}
                    />
                </>
            ) : (
                <>
                    <AccountNumberField
                        accountNumber={accountNumber}
                        handleAccountNumberChange={handleAccountNumberChange}
                        translated={translated}
                        validationMessage={validationMessages.accountNumberInput}
                        attentionMessage={duplicateAccountWarning}
                    />
                    <AccountNameField
                        accountName={accountName}
                        handleAccountNameChange={handleAccountNameChange}
                        translated={translated}
                        validationMessage={validationMessages.accountNameInput}
                    />
                </>
            )}
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {!isNettbank && (isInPaymentContext || accountName !== '' || accountNumber !== '') && (
                        <SelectReceiverStickyFooter
                            onClickButton={handleSubmit}
                            pending={false}
                            btnText={!isInPaymentContext ? translated.add : undefined}
                        />
                    )}
                    {isNettbank && (
                        <div className="nettbank-add-receiver-buttons">
                            <LinkButton onClick={onCancel}>{translated.cancel}</LinkButton>
                            <Button buttonType="negative" onClick={handleSubmit}>
                                {translated.add}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default NewReceiver;
